import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Snackbar,
  Alert
} from "@mui/material";
import { useParams } from "react-router-dom";
import useLabInternalStore from "../../store/useLabInternalStore";
import AllowedQuestionsSection from "../../components/AllowedQuestionsSection";
import { getFacultyEvaluationSetKeys } from "../../services/labInternals.api";

const emptySummary = {
  totalStudents: 0,
  submissionsReceived: 0,
  evaluated: 0,
  pending: 0,
  evaluationStarted: false
};

const statusChipProps = (status) => {
  switch (status) {
    case "NOT_SUBMITTED":
      return { label: "Not Submitted", color: "default" };
    case "SUBMITTED":
      return { label: "Submitted", color: "warning" };
    case "EVALUATED":
      return { label: "Evaluated", color: "success" };
    default:
      return { label: status, color: "default" };
  }
};

const buildStudentDisplayLabel = (student) => {
  const roll =
    student?.rollNumber != null && String(student.rollNumber).trim()
      ? String(student.rollNumber).trim()
      : "";
  const name =
    student?.name != null && String(student.name).trim()
      ? String(student.name).trim()
      : student?.displayName != null && String(student.displayName).trim()
        ? String(student.displayName).trim()
        : "";

  if (roll && name) return `${roll} - ${name}`;
  if (roll) return roll;
  if (name) return name;
  return student?.id || student?.studentId || "-";
};

const SummaryCard = ({ label, value }) => (
  <Card elevation={1} sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const FacultyLabDetailPage = () => {
  const { labId } = useParams();
  const [summary, setSummary] = React.useState(emptySummary);
  const [lab, setLab] = React.useState(null);
  const [students, setStudents] = React.useState([]);
  const [allowedQuestions, setAllowedQuestions] = React.useState([]);
  const [allowedError, setAllowedError] = React.useState("");
  const [isSavingAllowed, setIsSavingAllowed] = React.useState(false);

  const fetchFacultyLabDetails = useLabInternalStore((s) => s.fetchFacultyLabDetails);
  const fetchFacultyAllowedQuestions = useLabInternalStore((s) => s.fetchFacultyAllowedQuestions);
  const setAllowed = useLabInternalStore((s) => s.setFacultyAllowedQuestions);
  const triggerEval = useLabInternalStore((s) => s.triggerFacultyLabEvaluation);
  const facultyDetailsMap = useLabInternalStore((s) => s.facultyLabDetails);
  const detailsLoading = useLabInternalStore((s) => s.facultyDetailsLoading);
  const questionsLoading = useLabInternalStore((s) => s.facultyQuestionsLoading);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [evaluationSetKeys, setEvaluationSetKeys] = React.useState([]);

  React.useEffect(() => {
    getFacultyEvaluationSetKeys()
      .then(setEvaluationSetKeys)
      .catch(() => setEvaluationSetKeys([]));
  }, []);

  React.useEffect(() => {
    if (!labId) return;
    const run = async () => {
      try {
        await fetchFacultyLabDetails(labId);
        await fetchFacultyAllowedQuestions(labId);
        const data = facultyDetailsMap[labId];
        const labObj = data?.lab || null;
        const summaryObj = data?.studentSummary || data?.summary || emptySummary;
        const studentsArr = Array.isArray(data?.students) ? data.students : [];
        setLab(labObj);
        setSummary(summaryObj);
        setStudents(studentsArr);
        const allowedPayload = useLabInternalStore.getState().facultyAllowedQuestions[labId];
        const list = allowedPayload?.allowedQuestions || allowedPayload || [];
        const normalized = Array.isArray(list)
          ? list.map((entry) => ({
              questionId:
                entry?.questionId?._id ||
                entry?.questionId?.id ||
                entry?.questionId ||
                entry?.id,
              setKey: entry?.setKey ?? "",
              part1MaxMarks: entry?.part1MaxMarks ?? "",
              part2MaxMarks: entry?.part2MaxMarks ?? "",
            }))
          : [];
        setAllowedQuestions(normalized);
      } catch (err) {
        setSnackbar({ open: true, message: 'Unable to load lab details.', severity: 'error' });
      }
    };
    run();
  }, [labId, fetchFacultyLabDetails, fetchFacultyAllowedQuestions, facultyDetailsMap]);

  const [isEvaluating, setIsEvaluating] = React.useState(false);
  const handleStartEvaluation = async () => {
    try {
      setIsEvaluating(true);
      await triggerEval(labId);
      setSummary((prev) => ({ ...prev, evaluationStarted: true }));
      setSnackbar({ open: true, message: 'Evaluation started successfully.', severity: 'success' });
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setSnackbar({
        open: true,
        message:
          typeof apiMessage === 'string' && apiMessage.trim()
            ? apiMessage
            : 'Failed to start evaluation.',
        severity: 'error'
      });
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const disableStartEvaluation = detailsLoading || isEvaluating || summary.evaluationStarted;
  const isLabLocked = lab?.status === "ACTIVE" || lab?.status === "CLOSED";

  const handleSaveAllowedQuestions = async () => {
    if (!labId || isLabLocked) return;
    setIsSavingAllowed(true);
    setAllowedError("");
    try {
      const payload = allowedQuestions.map((item) => ({
        questionId: item.questionId,
        setKey: item.setKey,
        part1MaxMarks: Number(item.part1MaxMarks),
        part2MaxMarks: Number(item.part2MaxMarks),
      }));
      await setAllowed(labId, payload);
      setSnackbar({ open: true, message: 'Allowed questions updated successfully.', severity: 'success' });
    } catch (err) {
      setAllowedError(err?.response?.data?.message || 'Failed to save allowed questions.');
    } finally {
      setIsSavingAllowed(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Lab Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          disabled={disableStartEvaluation}
          onClick={handleStartEvaluation}
        >
          Start Evaluation
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Total Students" value={summary.totalStudents} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Submissions Received"
            value={summary.submissionsReceived}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Evaluated" value={summary.evaluated} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Pending" value={summary.pending} />
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Allowed Questions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure which Question Bank items are available for this lab.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleSaveAllowedQuestions}
              disabled={
                questionsLoading ||
                isSavingAllowed ||
                isLabLocked ||
                allowedQuestions.length === 0
              }
            >
              {isSavingAllowed ? "Saving..." : "Save Allowed Questions"}
            </Button>
          </Stack>

          {isLabLocked && (
            <Alert severity="info">
              Allowed questions cannot be modified when the lab is ACTIVE or CLOSED.
            </Alert>
          )}

          {allowedError && <Alert severity="error">{allowedError}</Alert>}

          <AllowedQuestionsSection
            value={allowedQuestions}
            onChange={setAllowedQuestions}
            disabled={isLabLocked || detailsLoading}
            showHeader={false}
            evaluationSetKeys={evaluationSetKeys}
          />
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ p: 2 }}>
        <TableContainer>
          <Table size="medium" aria-label="student evaluation table">
            <TableHead>
              <TableRow>
                <TableCell>Roll Number - Name</TableCell>
                <TableCell>Part 1 marks</TableCell>
                <TableCell>Part 2 marks</TableCell>
                <TableCell>Total Score</TableCell>
                <TableCell>Evaluation Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => {
                const chipProps = statusChipProps(student.status);
                return (
                  <TableRow key={student.id || student.studentId} hover>
                    <TableCell>{buildStudentDisplayLabel(student)}</TableCell>
                    <TableCell>{student.part1Marks ?? "-"}</TableCell>
                    <TableCell>{student.part2Marks ?? "-"}</TableCell>
                    <TableCell>{student.totalScore}</TableCell>
                    <TableCell>
                      <Chip size="small" {...chipProps} />
                    </TableCell>
                    <TableCell align="right">
                      <Button variant="outlined" size="small">
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacultyLabDetailPage;
